// src/auth/token-service.ts
import { env } from '@/config/env';
import type { LoginTokens, UserData } from './types';
import { decodeJwt } from './jwt';

const TOKEN_URL = `${env.apiBaseUrl}/connect/token`;
const USER_DATA_URL = `${env.apiBaseUrl}/api/Account/GetUserDataToken`;
const UPDATE_DEVICE_URL = `${env.apiBaseUrl}/api/Account/UpdateDeviceDetails`;

/** Thrown when GetUserDataToken returns 409 "User already logged in" — the
 *  backend enforces one active session per user. The caller may retry with the
 *  `skipConflict` option (the SkipConflict bypass header) for the same user. */
export class ActiveSessionError extends Error {
  constructor() {
    super('User already logged in');
    this.name = 'ActiveSessionError';
  }
}

/** Thrown when the `/connect/token` password/refresh grant returns a non-2xx.
 *  Carries the HTTP status so callers can branch — Flutter parity
 *  (login_repository_impl.dart): a 400 means bad credentials (probe
 *  CheckLockOutTime — it may be a lockout), a 401 means the account itself is
 *  archived/inactive (a DIFFERENT, non-probeable failure — show the
 *  contact-support copy, never the lockout check). */
export class TokenGrantError extends Error {
  status: number;
  constructor(status: number) {
    super(`token request failed: ${status}`);
    this.name = 'TokenGrantError';
    this.status = status;
  }
}

/** Thrown when the `GetUserDataToken` profile fetch does not produce a profile —
 *  a non-2xx response, a network error, or an unparseable body.
 *
 *  This used to be a soft `isFallback: true` alongside a JWT-claims-only user,
 *  and the auth-store installed that user anyway. The JWT carries NEITHER `role`
 *  NOR `tenantRoleId`, so the result was an authenticated session in which every
 *  role check is false: `isNurse` false, `isDoctor` false, `needsOnboarding`
 *  true, and — before the sibling fix in this change — sign-and-lock LOCKING,
 *  because the lock was `!isNurse`. A profile we could not load must send the
 *  user back to re-authenticate, not into the app with the most permissive
 *  defaults, so this is now a hard failure the caller cannot destructure away.
 *
 *  `status` is the HTTP status, or null for a network/parse error.
 *
 *  AVAILABILITY TRADE (deliberate, product owner's call): a transient
 *  GetUserDataToken failure that previously produced a degraded session now
 *  produces a FAILED LOGIN. A flaky profile endpoint therefore presents as
 *  "users cannot log in", not as "users log in with odd permissions". Whoever
 *  operates this should read a spike of these as a profile-endpoint incident.
 *
 *  NOT IMPLEMENTED (proposal, needs a decision before anyone builds it): `status`
 *  is carried precisely so a retry could be added cleanly later — retry ONCE, with
 *  no backoff loop, only when the failure is plausibly transient (`status === null`
 *  network error, or 500/502/503/504), and never for a definitive answer (401/403
 *  ⇒ the token is not good for this endpoint; 404 ⇒ no profile exists; 409 ⇒ an
 *  active session, which has its own recovery). Retrying a definitive failure only
 *  doubles the load on an endpoint that has already answered. */
export class ProfileUnavailableError extends Error {
  /** HTTP status, or null when the request never got an answer. */
  status: number | null;
  constructor(status: number | null, cause?: unknown) {
    super(`GetUserDataToken failed: ${status ?? 'network error'}`);
    this.name = 'ProfileUnavailableError';
    this.status = status;
    this.cause = cause;
  }
}

async function postToken(body: Record<string, string>): Promise<LoginTokens> {
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });
  if (!res.ok) {
    throw new TokenGrantError(res.status);
  }
  const json = (await res.json()) as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
  return {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresIn: json.expires_in,
  };
}

export function passwordGrant(username: string, password: string): Promise<LoginTokens> {
  return postToken({
    client_id: env.auth.clientId,
    client_secret: env.auth.clientSecret,
    grant_type: 'password',
    username,
    password,
    scope: env.auth.scope,
  });
}

export function refreshGrant(refreshToken: string): Promise<LoginTokens> {
  return postToken({
    client_id: env.auth.clientId,
    client_secret: env.auth.clientSecret,
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    scope: env.auth.scope,
  });
}

/** POST `/api/Account/UpdateDeviceDetails` — clears the backend single-session
 *  lock (`IsUserLoggedIn = false`, AccountController.cs) for `userId`, so a
 *  subsequent GetUserDataToken/login for the same user won't 409. AppType MUST
 *  be "Provider". Used by the DEV auto-takeover (auth-store) because the
 *  `SkipConflict` header is a no-op server-side (removed in Identity PROD-931),
 *  making the takeover the only real way past an active-session 409. Throws on
 *  non-2xx so the caller can fall back. */
export async function updateDeviceDetails(accessToken: string, userId: string): Promise<void> {
  const res = await fetch(UPDATE_DEVICE_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      AppType: 'Provider',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, appType: 'Provider', deviceId: 'web' }),
  });
  if (!res.ok) {
    throw new Error(`UpdateDeviceDetails failed: ${res.status}`);
  }
}

/** Fetch the provider's UserData: the JWT claims (always present) merged with the
 *  GetUserDataToken profile. Resolves ONLY with a real, loaded profile — anything
 *  else throws, so no caller can accidentally authenticate a session that carries
 *  no `role`/`tenantRoleId`. AppType MUST be "Provider".
 *
 *  - `throwOnActiveSession`: a 409 ("User already logged in") raises
 *    {@link ActiveSessionError}, unchanged, so the caller can recover with an
 *    UpdateDeviceDetails takeover (see auth-store). That is a SESSION CONFLICT,
 *    not a login failure, and must never be flattened into one.
 *  - Any other non-2xx, and any network/parse error, raise
 *    {@link ProfileUnavailableError}. A 409 WITHOUT `throwOnActiveSession` is
 *    just another non-2xx and raises it too: it was previously swallowed into a
 *    roleless session, which is the failure this change exists to remove.
 *
 *  NOTE: the old `SkipConflict` header was removed — it is a no-op server-side
 *  (Identity PROD-931), so it never bypassed the single-session lock; the
 *  recovery is the takeover, not a header. */
export async function fetchUserData(
  accessToken: string,
  { throwOnActiveSession = false }: { throwOnActiveSession?: boolean } = {},
): Promise<UserData> {
  const claims = decodeJwt(accessToken);
  const base: UserData = {
    userId: claims?.sub ?? '',
    organizationId: claims?.orgId ?? '',
    email: claims?.email,
    firstName: claims?.given_name,
    lastName: claims?.family_name,
    timeZone: claims?.zoneinfo,
  };
  try {
    const res = await fetch(USER_DATA_URL, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        AppType: 'Provider',
      },
    });
    if (res.status === 409 && throwOnActiveSession) {
      throw new ActiveSessionError();
    }
    if (!res.ok) throw new ProfileUnavailableError(res.status);
    const profile = (await res.json()) as Partial<UserData>;
    return { ...base, ...profile, userId: base.userId, organizationId: base.organizationId };
  } catch (e) {
    // A session conflict is the caller's to recover from — never re-badge it as a
    // profile failure, or the takeover dialog stops being reachable.
    if (e instanceof ActiveSessionError) throw e;
    if (e instanceof ProfileUnavailableError) throw e;
    // fetch() itself rejected (offline / DNS / CORS / abort) or the body did not
    // parse: no answer at all, so no status.
    throw new ProfileUnavailableError(null, e);
  }
}
